import * as React from 'react';
import classnames from 'classnames';
import { SelectedState, ServersByLocation, DeliveryLocation } from '@getnoble/noble-tip-splitting-utils';
import { DeliveryLocationFields } from '../../../../fragments/__generated__/DeliveryLocationFields';
import { Capacity } from './Capacity';
import './DeliveryLayoutRow.scss';
const Checkmark = require('./Checkmark.png');

interface DeliveryLayoutRowProps<Location extends DeliveryLocation | DeliveryLocationFields> {
  fulfillmentCenterName: string;
  locations: Location[];
  parentNames: string[];
  selectedStatus?: {
    [id: string]: SelectedState;
  };
  serverStatus?: ServersByLocation;
  showHelpText: boolean;
  onToggle?(layout: Location);
  createUrl?(location: Location);
  onClickLocation?(location: Location);
  onShiftClickOverLocation?(location: Location);
}

const classNameForSelectedState = (state: SelectedState): string => {
  switch (state) {
    case 'selected':
      return 'delivery-layout-icon--selected';
    case 'partial':
      return 'delivery-layout-icon--partially-selected';
    case 'unselected':
      return 'delivery-layout-icon--unselected';
  }
};

const SelectionIcon = ({ selectedStatus, onToggle }: { selectedStatus: SelectedState; onToggle() }) => {
  return (
    <div onClick={onToggle} className={classnames('delivery-layout-icon', classNameForSelectedState(selectedStatus))}>
      {selectedStatus === 'selected' ? <img src={Checkmark} /> : ''}
    </div>
  );
};

interface DeliveryLocationProps<Location extends DeliveryLocation | DeliveryLocationFields> {
  onToggle?(location: Location);
  createUrl?(location: Location);
  onClickLocation?(location: Location);
  serverStatus?: ServersByLocation;
  selectedStatus?: {
    [id: string]: SelectedState;
  };
  location: Location;
  onShiftClickOverLocation?(location: Location);
}

const DeliverySection = <Location extends DeliveryLocation | DeliveryLocationFields>({
  createUrl,
  location,
  selectedStatus,
  onClickLocation,
  onToggle,
  serverStatus,
  onShiftClickOverLocation,
}: DeliveryLocationProps<Location>) => {
  const link = createUrl ? createUrl(location) : undefined;

  const onToggleMapped = React.useCallback(() => {
    onToggle(location);
  }, [location, onToggle]);

  const onClickLocationWrapped = React.useCallback(
    (e) => {
      if ([...e.target.classList].includes('delivery-layout-icon')) {
        e.preventDefault();
      } else if (onClickLocation) {
        onClickLocation(location);
      }
    },
    [location, onClickLocation]
  );

  const onPointerEnter = React.useCallback(
    (e) => {
      if (e.shiftKey && e.pressure > 0) {
        onShiftClickOverLocation(location);
      }
    },
    [location, onShiftClickOverLocation]
  );

  const server = serverStatus && serverStatus[location.id];
  const isAssignedToAnotherServer = serverStatus ? !!server : false;
  const className = classnames(
    'delivery-layout-location',
    isAssignedToAnotherServer && 'delivery-layout-location-disabled'
  );

  return (
    <a onPointerEnter={onPointerEnter} onClickCapture={onClickLocationWrapped} href={link} className={className}>
      {!!(selectedStatus && onToggle) && !isAssignedToAnotherServer && (
        <SelectionIcon selectedStatus={selectedStatus[location.id]} onToggle={onToggleMapped} />
      )}
      {location.name}
      <Capacity number={location.capacity} />
      {server && <div className='delivery-layout-location-server'>Assigned to: {server.name}</div>}
    </a>
  );
};

export const DeliveryLayoutRow = <Location extends DeliveryLocation | DeliveryLocationFields>(
  props: DeliveryLayoutRowProps<Location>
) => {
  const {
    selectedStatus,
    onToggle,
    onClickLocation,
    fulfillmentCenterName,
    parentNames,
    locations,
    serverStatus,
    showHelpText,
    onShiftClickOverLocation,
  } = props;
  const title = [fulfillmentCenterName, ...parentNames].join(' / ');

  return (
    <div className='mb-3'>
      <h3>{title}</h3>
      {showHelpText && (
        <p className='delivery-no-selection'>
          Click a location to view the catergory below or click the white circle to assign it.
        </p>
      )}
      <div className='delivery-layout-children delivery-no-selection'>
        {locations.map((location) => (
          <DeliverySection
            location={location}
            createUrl={props.createUrl}
            key={location.id}
            selectedStatus={selectedStatus}
            onToggle={onToggle}
            onClickLocation={onClickLocation}
            serverStatus={serverStatus}
            onShiftClickOverLocation={onShiftClickOverLocation}
          />
        ))}
      </div>
    </div>
  );
};
